import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { AnimateIn, SectionRow } from '../components/Layout';
import { hero, homeSections } from '../data/mock';

const AUTO_REVEAL_DELAY_MS = 2500;
const SCROLL_REVEAL_THRESHOLD_PX = 50;
const SCROLL_FADE_DISTANCE_PX = 1000;
const PARALLAX_RATE = 0.15;
const PARALLAX_SCALE_RATE = 0.00005;

const HomePage = () => {
  const heroImageRef = useRef(null);
  const textRevealedRef = useRef(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const reveal = () => {
      textRevealedRef.current = true;
      setScrollProgress(1);
    };

    const autoRevealTimer = setTimeout(reveal, AUTO_REVEAL_DELAY_MS);

    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        const windowHeight = window.innerHeight;

        if (scrollY > SCROLL_REVEAL_THRESHOLD_PX && !textRevealedRef.current) {
          clearTimeout(autoRevealTimer);
          reveal();
        }

        if (heroImageRef.current && scrollY < windowHeight * 2) {
          heroImageRef.current.style.transform =
            `translateY(${scrollY * PARALLAX_RATE}px) scale(${1 + scrollY * PARALLAX_SCALE_RATE})`;
        }

        if (!textRevealedRef.current) {
          setScrollProgress(Math.min(scrollY / SCROLL_FADE_DISTANCE_PX, 1));
        }

        ticking = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(autoRevealTimer);
    };
  }, []);

  const textRevealed = textRevealedRef.current || scrollProgress >= 1;

  return (
    <div>
      {/* ─── Hero Section ─── */}
      <section className="hero-reimagined">
        <div className="hero-img-container">
          <img
            ref={heroImageRef}
            src="https://customer-assets.emergentagent.com/job_subsense-lab/artifacts/tu8yrpf4_image.png"
            alt="Mithara Institute Inc."
            className="hero-img-full"
          />
          <div className="hero-overlay-bottom" style={{
            opacity: Math.min(scrollProgress * 0.7, 0.7)
          }} />
        </div>

        <div className="hero-text-bottom" style={{
          opacity: scrollProgress,
          transform: `translateY(${(1 - scrollProgress) * 60}px)`,
          transition: textRevealed ? 'opacity 1.5s cubic-bezier(0.16, 1, 0.3, 1), transform 1.5s cubic-bezier(0.16, 1, 0.3, 1)' : 'none',
        }}>
          <div className="hero-text-radical">
            <div className="hero-headline-radical">
              <span className="hero-label">Mithara Institute Inc.</span>
              <h1 className="hero-title-radical">{hero.headline}</h1>
            </div>
            
            <div className="hero-meta-radical">
              <p className="hero-desc-radical">{hero.description}</p>
              
              <Link to="/research" className="cta-radical">
                <span className="cta-radical-text">{hero.cta}</span>
                <span className="cta-radical-icon">↗</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section One: Why Mithara ─── */}
      <SectionRow 
        number={homeSections.why.number}
        label={homeSections.why.label}
        linkText={homeSections.why.link.text}
        linkHref={homeSections.why.link.href}
      />

      <section className="sec-light sec-pad epistemic-grid">
        <div className="inner">
          <div className="grid-2">
            <div>
              <AnimateIn>
                <h2 className="heading-lg">{homeSections.why.heading}</h2>
              </AnimateIn>
            </div>
            <div>
              {homeSections.why.cols.map((col, i) => (
                <AnimateIn key={col} delay={i * 150}>
                  <p className="body-text" style={{ marginBottom: i === 0 ? '28px' : '0' }}>
                    {col}
                  </p>
                </AnimateIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section Two: Research Areas ─── */}
      <SectionRow
        number={homeSections.areas.number}
        label={homeSections.areas.label}
        linkText={homeSections.areas.link.text}
        linkHref={homeSections.areas.link.href}
      />

      <section className="sec-white sec-pad-lg epistemic-grid">
        <div className="inner">
          <AnimateIn>
            <h2 className="heading-xl" style={{ marginBottom: '16px' }}>
              {homeSections.areas.heading}
            </h2>
          </AnimateIn>
          <AnimateIn delay={100}>
            <p className="body-text" style={{ marginBottom: '80px' }}>
              {homeSections.areas.description}
            </p>
          </AnimateIn>

          <div className="grid-4">
            {homeSections.areas.cards.map((card, i) => {
              const cardImages = [
                'https://customer-assets.emergentagent.com/job_subsense-lab/artifacts/htkqvse9_split_enhanced_1.jpg',
                'https://customer-assets.emergentagent.com/job_subsense-lab/artifacts/ye8wmqzq_split_enhanced_2.jpg',
                'https://customer-assets.emergentagent.com/job_subsense-lab/artifacts/s1usg5a4_split_enhanced_3.jpg',
                'https://customer-assets.emergentagent.com/job_subsense-lab/artifacts/htkqvse9_split_enhanced_1.jpg'
              ];
              
              return (
                <AnimateIn key={card.title} delay={i * 120} animation="fade-up">
                  <Link to={card.href} className="card-dark card-reveal" style={{
                    '--card-bg-image': `url(${cardImages[i]})`
                  }}>
                    <div className="card-image-layer" />
                    <div className="card-content-layer">
                      <div className="card-top">
                        <div className="card-label">{card.label}</div>
                        <div className="card-value">{card.value}</div>
                      </div>
                      <div className="card-mid">
                        <div className="card-desc">{card.desc}</div>
                      </div>
                      <div className="card-bottom">
                        <h3 className="card-title">{card.title}</h3>
                        <span className="card-link">
                          Learn More <ArrowUpRight size={14} />
                        </span>
                      </div>
                    </div>
                  </Link>
                </AnimateIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Section Three: Impact ─── */}
      <SectionRow
        number={homeSections.impact.number}
        label={homeSections.impact.label}
      />

      <section className="sec-sage sec-pad-lg">
        <div className="inner">
          <AnimateIn>
            <h2 className="heading-xl" style={{ marginBottom: '32px' }}>
              {homeSections.impact.heading}
            </h2>
          </AnimateIn>
          
          <div className="grid-2" style={{ marginBottom: '60px' }}>
            {homeSections.impact.cols.map((col, i) => (
              <AnimateIn key={i} delay={i * 120}>
                <p className="body-text">
                  {col}
                </p>
              </AnimateIn>
            ))}
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '80px', 
            flexWrap: 'wrap',
            paddingTop: '40px',
            borderTop: '1px solid rgba(16,16,16,0.1)'
          }}>
            {homeSections.impact.stats.map((stat, i) => (
              <AnimateIn key={i} delay={i * 120}>
                <div>
                  <div className="stat-huge">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              </AnimateIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section Four: Applications ─── */}
      <SectionRow
        number={homeSections.applications.number}
        label={homeSections.applications.label}
        linkText={homeSections.applications.link.text}
        linkHref={homeSections.applications.link.href}
      />

      <section className="sec-white sec-pad-lg epistemic-grid">
        <div className="inner">
          <AnimateIn>
            <h2 className="heading-xl" style={{ marginBottom: '16px' }}>
              {homeSections.applications.heading}
            </h2>
          </AnimateIn>
          <AnimateIn delay={100}>
            <p className="body-text" style={{ marginBottom: '60px' }}>
              {homeSections.applications.description}
            </p>
          </AnimateIn>

          {homeSections.applications.items.map((item, i) => (
            <AnimateIn key={item.name} delay={i * 120}>
              <div className="offer-row">
                <div className="offer-name">{item.name}</div>
                <div className="offer-desc">{item.desc}</div>
                <div className="offer-meta">
                  <div className="offer-meta-label">{item.meta.label}</div>
                  <div className="offer-meta-value">{item.meta.value}</div>
                  <Link to="/framework" className="offer-link">
                    Learn More <ArrowUpRight size={12} />
                  </Link>
                </div>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>
    </div>
  );
};

export default HomePage;
